import { indexedMap } from './indexedMap';
import { indexedSet } from './indexedSet';
export const createDirectedGraph = () => {
  // adjacency lists
  const outgoingEdges = indexedSet();
  const incomingEdges = indexedSet();

  // edge metadata
  const edgeMetadata = indexedMap();

  // nodes
  const nodes = new Map();

  // basic node operations
  const hasNode = id => nodes.has(id);
  const getNode = id => nodes.get(id);
  const setNode = (id, node) => {
    nodes.set(id, node);
    return node;
  };

  // edge metadata
  const getEdgeMetadata = (sourceId, targetId) => edgeMetadata.get(sourceId, targetId);
  const setEdgeMetadata = (sourceId, targetId, metadata) => {
    var _outgoingEdges$get;
    // Check if the edge exists before setting metadata
    if (!((_outgoingEdges$get = outgoingEdges.get(sourceId)) !== null && _outgoingEdges$get !== void 0 && _outgoingEdges$get.has(targetId))) {
      throw Error(`Attempting to set metadata for non-existent edge: ${sourceId} -> ${targetId}`);
    }
    edgeMetadata.set(sourceId, targetId, metadata);
  };

  // edge operations
  const hasEdge = (sourceId, targetId) => {
    var _outgoingEdges$get2;
    return !!((_outgoingEdges$get2 = outgoingEdges.get(sourceId)) !== null && _outgoingEdges$get2 !== void 0 && _outgoingEdges$get2.has(targetId));
  };
  function addEdge(sourceId, targetId, ...args) {
    // At least one node must exist to add an edge.
    // This allows for "partial" edges but still ensures that we don't
    // leak memory tracking edges between nonexistent nodes.
    // The traversal utility skips edges without a destination node.
    if (!hasNode(sourceId) && !hasNode(targetId)) {
      throw Error('At least one node must exist to add an edge');
    }

    // Register the edge in source's outgoing and target's incoming
    outgoingEdges.add(sourceId, targetId);
    incomingEdges.add(targetId, sourceId);

    // Set the edge's metadata if provided
    if (args.length > 0) {
      setEdgeMetadata(sourceId, targetId, args[0]);
    }
  }
  const removeEdge = (sourceId, targetId) => {
    // Remove edge from both outgoing and incoming adjacency lists
    outgoingEdges.delete(sourceId, targetId);
    incomingEdges.delete(targetId, sourceId);

    // Remove metadata, which is only stored in the outgoing direction
    edgeMetadata.delete(sourceId, targetId);
  };
  const removeNode = id => {
    // Remove outgoing edges
    for (const targetId of outgoingEdges.get(id) || []) {
      removeEdge(id, targetId);
    }

    // Remove incoming edges
    for (const sourceId of incomingEdges.get(id) || []) {
      removeEdge(sourceId, id);
    }
    nodes.delete(id);
  };
  return {
    edge: {
      count: () => outgoingEdges.count(),
      has: hasEdge,
      add: addEdge,
      remove: removeEdge,
      getOutgoing: id => outgoingEdges.get(id) || new Set(),
      getIncoming: id => incomingEdges.get(id) || new Set(),
      getAllOutgoing: () => outgoingEdges.entries(),
      getAllIncoming: () => incomingEdges.entries(),
      metadata: {
        get: getEdgeMetadata,
        set: setEdgeMetadata
      }
    },
    node: {
      count: () => nodes.size,
      get: getNode,
      set: setNode,
      has: hasNode,
      remove: removeNode,
      entries: () => nodes.entries()
    }
  };
};